import React, { useCallback, useEffect, useState } from "react";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import {
  AdminEmptyState,
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminReconciliation } from "../store/admin";
import { formatCurrency, formatDate, statusBadgeClass } from "./components/marketplace/marketplaceUtils";

const RUN_FIELDS = [
  { key: "checked", label: "Checked" },
  { key: "fixed", label: "Fixed" },
  { key: "released", label: "Released" },
  { key: "resolved", label: "Resolved" },
  { key: "issues", label: "Issues" },
];

const Reconciliation = () => {
  const {
    reconciliationIssues: issues,
    reconciliationLoading,
    reconciliationError,
    reconciliationRun,
    reconciliationMutationLoading,
    reconciliationMutationError,
    fetchReconciliationIssues,
    runReconciliation,
  } = useAdminReconciliation();

  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    fetchReconciliationIssues();
  }, [fetchReconciliationIssues]);

  const handleRun = async () => {
    const result = await runReconciliation();
    if (!result.success) {
      showToast(result.error || "Reconciliation failed", false);
      return;
    }
    showToast("Reconciliation completed", true);
    await fetchReconciliationIssues();
  };

  return (
    <div>
      <AdminNavBar title="Reconciliation" />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <AdminSectionHeader
          title="Payment Reconciliation"
          description="Compare local and gateway payment states and resolve mismatches."
          count={`${issues.length} unresolved issues`}
          actions={
            <button
              className={adminPrimaryButton}
              onClick={handleRun}
              disabled={reconciliationMutationLoading}
            >
              {reconciliationMutationLoading ? "Running…" : "Run Reconciliation"}
            </button>
          }
        />

        {reconciliationRun && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {RUN_FIELDS.map((field) => (
              <div
                key={field.key}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {field.label}
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-900">
                  {reconciliationRun[field.key] ?? 0}
                </p>
              </div>
            ))}
          </div>
        )}

        {(reconciliationError || reconciliationMutationError) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {reconciliationError || reconciliationMutationError}
          </div>
        )}

        {reconciliationLoading ? (
          <AdminLoadingState />
        ) : issues.length === 0 ? (
          <AdminEmptyState
            title="No unresolved issues"
            description="Run reconciliation to compare local and gateway payment states."
          />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Issue
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Reference
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Local / Remote
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Amount
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Description
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((issue) => (
                    <tr key={issue.id} className="border-b border-slate-100">
                      <td className="px-4 py-2">
                        <span className={`badge ${statusBadgeClass(issue.issueType)}`}>
                          {issue.issueType}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{issue.reference}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">
                        {issue.localStatus || "—"} / {issue.remoteStatus || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div>{formatCurrency(issue.amountLocal)}</div>
                        <div className="text-xs text-slate-500">
                          remote {formatCurrency(issue.amountRemote)}
                        </div>
                      </td>
                      <td className="max-w-[320px] px-4 py-2 text-sm text-slate-600">
                        {issue.description}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {formatDate(issue.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}
      </AdminPageBody>
    </div>
  );
};

export default Reconciliation;
